import React, {
  createContext,
  useContext,
  useMemo,
  ReactNode,
  useEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "./useLocalStorage";
import axiosInstance from "../network/axiosInstance";
import { getMyRegistrations } from "../network/Apiservices";

interface User {
  username: string;
  access_token?: string;
  refresh_token?: string;
  isRegistered?: boolean;
  email?: string;
}

interface AuthContextType {
  user: User | null;
  login: (data: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  const [user, setUser] = useLocalStorage<User | null>("user", null);
  const navigate = useNavigate();

  const login = async (data: User): Promise<void> => {
    setUser(data);
    axiosInstance.defaults.headers.common.Authorization = `Bearer ${data.access_token}`;
    navigate("/");
  };

  const logout = (): void => {
    setUser(null);
    navigate("/", { replace: true });
  };

  const checkMyRegistration = async () => {
    try {
      let response: any = await getMyRegistrations();
      let isRegistered = response?.registrations?.length > 0;
      if (user != null) setUser({ ...user, isRegistered });
    } catch (e: any) {
      console.log("failed to fetch", e);
    }
  };
  useEffect(() => {
    if (user != null) {
      checkMyRegistration();
    } else {
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(
    () => ({
      user,
      login,
      logout,
    }),
    [user] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
